import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

import type { Profile } from '@/types/profile';

import { api } from '@/lib/api';
import { tokenSubject } from '@/lib/auth';
import {
  ALL_OFF, ALL_ON, blockingDependents, isEnabled, levelOf, resolveStoredCapabilities,
  type CapabilityId, type CapabilityState,
} from '@/lib/capabilities';

/**
 * The teacher's capability levels, available anywhere a gate needs them.
 *
 * ABOVE THE ROUTER, because a gate belongs to a control rather than to a page: the version pager
 * is mounted twenty-two times across the task editor and the test panel, and a prop would be
 * twenty-two places to remember it. The same reasoning `VersionSelector` already gives for reading
 * its driven-axis state off the versions instead of taking it as a prop.
 *
 * ## It fails OPEN on an ERROR, and starts CLOSED while loading
 *
 * Those are two different moments and they want opposite answers.
 *
 * If the fetch never lands, every capability reads as on. A gate is a teaching aid, not a
 * permission: the worst case for a wrong "on" is a teacher seeing a control they were not ready
 * for, and the worst case for a wrong "off" is a control vanishing from a document that uses it.
 * Not symmetric, so an error leans open.
 *
 * WHILE the fetch is in flight is the opposite. Seeding `ALL_ON` there meant a new teacher — whose
 * stored record resolves to `ALL_OFF` — watched every gated control paint and then disappear, with
 * a window in between where it could be clicked. Seeding off makes the same moment additive:
 * controls fill in as the answer arrives, which is how every other loading state in the app reads,
 * and nothing is ever taken away from under a cursor.
 *
 * Outside the provider entirely, `FALLBACK` still reads on — a harness with no provider is not a
 * teacher with nothing turned on.
 */
interface CapabilitiesValue {
  state: CapabilityState;
  /** Whether this capability is at `atLeast` or higher. */
  has: (id: CapabilityId, atLeast?: number) => boolean;
  level: (id: CapabilityId) => number;
  /** Moves a capability to a rung, raising prerequisites with it. Refused if something needs it. */
  setLevel: (id: CapabilityId, rung: number) => Promise<void>;
  /** What holds this capability at its current rung, or an empty list when it can be lowered. */
  blockedBy: (id: CapabilityId, rung: number) => CapabilityId[];
}

/**
 * How hard to try for the profile before deciding it cannot be read.
 *
 * Three attempts over ~300ms: enough to ride out a backend still coming up or a single dropped
 * request, and short enough that a teacher whose profile is genuinely unreachable is not left
 * looking at a stripped builder while it tries.
 */
const PROFILE_ATTEMPTS = 3;
const PROFILE_RETRY_MS = 100;

/**
 * THE LAST ANSWER THE SERVER GAVE, so a failed load never has to GUESS.
 *
 * The capability state is not a thing to be inferred — it is simply what this teacher saved, and
 * the only reason the app does not know it at paint time is that it has to be fetched. Keeping the
 * last answer turns three problems into none:
 *
 *   - a failed fetch no longer falls back to everything-on, which opened every gate for the
 *     session on a single transient error
 *   - a returning teacher gets their own view on the FIRST frame, with no closed-then-filling-in
 *     flash while the request is out
 *   - a brand new account still gets `ALL_OFF`, because it has nothing cached
 *
 * KEYED BY THE TOKEN'S SUBJECT, so a shared browser cannot hand one teacher's gates to another:
 * a cache written for someone else is simply not read. The server's answer always wins when it
 * arrives, so a stale cache costs one render, never a decision.
 *
 * Every access is wrapped: storage throws in a private window and can come back empty with site
 * data cleared, and none of that is worth a broken builder.
 */
const CACHE_PREFIX = 'agavi.capabilities.';

function cacheKey(): string | null {
  const sub = tokenSubject();
  return sub ? CACHE_PREFIX + sub : null;
}

function readCache(): CapabilityState | null {
  const key = cacheKey();
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    // A record of numbers, or nothing. Anything else is treated as absent rather than trusted.
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return null;
    return Object.fromEntries(
      Object.entries(parsed as Record<string, unknown>)
        .filter(([, v]) => typeof v === 'number' && Number.isInteger(v)),
    ) as CapabilityState;
  } catch {
    return null;
  }
}

function writeCache(state: CapabilityState): void {
  const key = cacheKey();
  if (!key) return;
  try { localStorage.setItem(key, JSON.stringify(state)); } catch { /* storage is a convenience */ }
}

const CapabilitiesContext = createContext<CapabilitiesValue | null>(null);

export function CapabilitiesProvider({ children }: { children: ReactNode }) {
  /*
   * The last answer the server gave, or closed. Never everything-on: a first paint that guesses
   * OPEN is the one that shows a control and then takes it away.
   */
  const [state, setState] = useState<CapabilityState>(() => readCache() ?? ALL_OFF);

  useEffect(() => {
    let live = true;

    /*
     * RETRIED BEFORE IT FAILS OPEN, because one blip used to cost the whole session.
     *
     * Failing open on the FIRST rejection made a transient error — a request racing the backend
     * coming back up, which `handleResponse` documents as a real occurrence — turn every
     * capability on until the next reload. That is the worst failure this provider has: it is
     * silent, it lasts the whole session, and it looks exactly like the gates not working. The
     * owner met it as flow "returning on occasion" after adding a block, unreproducible and
     * surviving a hard refresh — which is what a per-load dice roll looks like from outside.
     *
     * Fail-open is still right for a profile that genuinely cannot be read: a wrong "off" would
     * take controls away from documents that use them. It is simply not right for the first
     * stumble, and the two were conflated.
     */
    void (async () => {
      for (let attempt = 0; attempt < PROFILE_ATTEMPTS; attempt++) {
        try {
          const p = await api.get<Profile>('/profile');
          const next = resolveStoredCapabilities(p.capabilities);
          writeCache(next);
          if (live) setState(next);
          return;
        } catch {
          // Last attempt: fall through to the open state below rather than retrying for ever.
          if (attempt === PROFILE_ATTEMPTS - 1) break;
          await new Promise((r) => { setTimeout(r, PROFILE_RETRY_MS * (attempt + 1)); });
          if (!live) return;
        }
      }
      /*
       * Out of attempts. A teacher we have seen before keeps what they had — the cache is already
       * in `state` from the seed, so there is nothing to do and nothing to guess. Only a teacher
       * we have never seen falls open, which is the case the original rule was written for: we
       * genuinely do not know, and a wrong "off" would take controls away from a document.
       */
      if (live && readCache() === null) setState(ALL_ON);
    })();

    return () => { live = false; };
  }, []);

  const setLevel = useCallback(async (id: CapabilityId, rung: number) => {
    /*
     * A PLAIN WRITE, and it stays one. The note here used to promise `withPrerequisites` once
     * `blockingDependents` became the control's `disabled` — that happened, and the promise is
     * no longer the right fix.
     *
     * The page cannot make an illegal move: `canTurnOn` refuses until the prerequisites are met,
     * `canTurnOff` until nothing depends on the drop, and `Switch` renders a real
     * `<button disabled>` rather than an `aria-disabled` label. So a switch that moved on its own
     * would only ever be covering for a state the page already refuses to produce.
     *
     * The case that DOES arise is a stored record made illegal by a change to the registry, and
     * that is caught on the way IN by `clampToPrerequisites` — one funnel every reader goes
     * through, rather than one writer that a second caller could bypass.
     */
    const next = { ...state, [id]: rung };
    // Optimistic: the page is a list of switches and a round trip per flip would feel broken.
    setState(next);
    try {
      await api.patch<Profile>('/profile/capabilities', { capabilities: next });
    } catch {
      setState(state);
    }
  }, [state]);

  const value = useMemo<CapabilitiesValue>(() => ({
    state,
    has: (id, atLeast = 1) => isEnabled(state, id, atLeast),
    level: (id) => levelOf(state, id),
    setLevel,
    blockedBy: (id, rung) => blockingDependents(state, id, rung),
  }), [state, setLevel]);

  return <CapabilitiesContext.Provider value={value}>{children}</CapabilitiesContext.Provider>;
}

/**
 * The capability context, or an all-on stand-in outside the provider.
 *
 * The stand-in is what keeps every existing test and story rendering: a block mounted without the
 * provider is not a teacher with nothing turned on, it is a harness that has no opinion. Failing
 * open here is the same call the provider makes for a failed fetch.
 */
export function useCapabilities(): CapabilitiesValue {
  const ctx = useContext(CapabilitiesContext);
  return ctx ?? FALLBACK;
}

/** Shorthand for the common case: one capability, at one rung or higher. */
export function useCapability(id: CapabilityId, atLeast = 1): boolean {
  return useCapabilities().has(id, atLeast);
}

/**
 * WHETHER TO RENDER ONE EXTRA BLOCK SETTING — the capability, OR this block already using it.
 *
 * Hiding a control a block depends on would leave the editor contradicting the block: a Numerical
 * question still graded on three significant figures, still wearing its "3 s.f." chip, and no row
 * on screen saying so or able to clear it. So a setting off its default stays visible however the
 * capability stands. Gating the affordance to ADOPT a feature is the whole point; hiding a fact
 * about the block in front of the teacher never was.
 *
 * `inUse` is read at BOTH ends — the live value and `initial.current`, the mount snapshot every
 * block editor already holds and none of them reassigns. The second term is what stops the row
 * vanishing under the pointer the moment it is unchecked, which would also make an accidental
 * click unrecoverable without a trip to Settings. It comes back for good on the next open.
 *
 * A fresh block sets every one of these to its default (`false`, `'linear'`, `'magnetic'`), so
 * nothing a new block does can put a field off default except a control this hides. The beginner
 * view is therefore exact, and this clause only ever grandfathers blocks authored before the
 * capability came off.
 */
export function useExtraBlockSetting(inUse: boolean): boolean {
  return useCapability('extraBlockSettings') || inUse;
}

const FALLBACK: CapabilitiesValue = {
  state: ALL_ON,
  has: () => true,
  level: (id) => levelOf(ALL_ON, id),
  setLevel: async () => {},
  blockedBy: () => [],
};
